/** @jsxImportSource @emotion/react */

import { CreditCard, Globe, LayoutPanelTop, Monitor, Plus, TriangleAlert } from 'lucide-react';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useFeature } from 'shared/hooks/use-feature';
import { AsyncState } from 'shared/utils/async-state';
import UDAsyncContent from 'ud-ui/components/async-content';
import UDEmptyState from 'ud-ui/components/empty-state';

import { useAppDispatch } from 'store/hooks';
import { history } from 'store/store';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import UDButton from 'ud-ui/components/button';
import UDTippy from 'ud-ui/components/tippy';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { resetPressedState, setActiveBoard, setActiveTemplate } from 'modules/template-editor/store/templates/templates';
import ImportBoardTemplateButton from '../components/elements/import-board-template-button';
import ListContainer from '../components/elements/list-container';
import TemplateItem from '../components/elements/template-item';
import useCreateModal from '../hooks/modals/template/create';
import useSuccessCopyModal from '../hooks/modals/template/success-copy';
import { useLoadTemplates } from '../hooks/use-load-templates';
import { useOwnerBoardTemplates } from '../hooks/use-owner-board-templates';

function Templates() {
  const { t } = useLingui();
  const state = useLocation().state;
  const dispatch = useAppDispatch();
  const { ownerId, templates, state: templatesState, refetch: refetchTemplates } = useOwnerBoardTemplates();
  const { modal: createModal, open: openCreateModal } = useCreateModal();
  const { modal: successCopyModal, open: openSuccessCopyModal } = useSuccessCopyModal();

  const { isEnabled: hasObsIntegration, isFetching: isFeatureFetching } = useFeature('OBS_INTEGRATION');

  const { setHeaderContent } = useMainLayout();

  useLoadTemplates();

  const breadcrumb = useMemo(
    () => <UDBreadcrumbs className="my-10" breadcrumbs={[{ label: t`Трансляции`, to: Pages.TEMPLATE.INDEX }]} />,
    [t],
  );

  const openCatalog = useCallback(() => {
    history.push(Pages.TEMPLATE.CATALOG.INDEX);
  }, []);

  const openSubscription = useCallback(() => {
    history.push(Pages.SETTINGS.builders.subscription('OBS_INTEGRATION'));
  }, []);

  const featureLockedTooltip = useMemo(
    () => (!isFeatureFetching && !hasObsIntegration ? t`Не доступно в вашей подписке` : undefined),
    [hasObsIntegration, isFeatureFetching, t],
  );

  useEffect(() => {
    if (state && state.isCoppedTemplate) {
      openSuccessCopyModal();
    }

    dispatch(setActiveTemplate({ id: undefined }));
    dispatch(setActiveBoard({ id: undefined }));
    dispatch(resetPressedState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    setHeaderContent(breadcrumb);
    return () => setHeaderContent(null);
  }, [breadcrumb, setHeaderContent]);

  // Пока не знаем тариф, кнопки строк не определены — держим ветку загрузки.
  const listState = useMemo<AsyncState>(
    () => (isFeatureFetching ? { status: 'loading', isRefreshing: false } : templatesState),
    [isFeatureFetching, templatesState],
  );

  return (
    <div className="flex h-full flex-col gap-4 px-6 pt-4">
      <div className={'flex w-full justify-end'} style={{ gap: 24 }}>
        {!isFeatureFetching && !hasObsIntegration && (
          <UDButton variant={'primary'} className="mr-auto flex items-center" onClick={openSubscription}>
            <CreditCard size={16} className={'mr-2'} />
            <Trans>Оформить подписку</Trans>
          </UDButton>
        )}
        <UDButton variant={'dark'} className="flex items-center" onClick={() => history.push(Pages.DEVICES.INDEX)}>
          <Monitor size={16} className={'mr-2'} />
          <Trans>Устройства</Trans>
        </UDButton>
        <UDTippy content={featureLockedTooltip} placement={'bottom'}>
          <span className="inline-flex">
            <UDButton
              variant={'dark'}
              className="flex items-center"
              onClick={openCreateModal}
              disabled={isFeatureFetching || !hasObsIntegration}
            >
              <Plus size={16} className="mr-2" />
              <Trans>Добавить шаблон</Trans>
            </UDButton>
          </span>
        </UDTippy>
        <ImportBoardTemplateButton
          ownerId={ownerId}
          disabled={isFeatureFetching || !hasObsIntegration}
          lockedTooltip={featureLockedTooltip}
        />
        <UDButton variant={'primary'} className="flex items-center" onClick={openCatalog}>
          <Globe size={16} className={'mr-2'} />
          <Trans>Готовые шаблоны</Trans>
        </UDButton>
      </div>
      <ListContainer className="flex-1" title={t`Ваши шаблоны для трансляции`} contentProps={{ className: 'flex min-h-0 flex-col' }}>
        <UDAsyncContent
          className="flex-1"
          state={listState}
          error={
            <UDEmptyState
              className="max-w-xl px-6"
              icon={<TriangleAlert size={28} />}
              title={t`Не удалось загрузить шаблоны`}
              subtitle={t`Проверьте соединение и попробуйте ещё раз`}
              action={
                <UDButton variant={'primary'} onClick={refetchTemplates}>
                  <Trans>Повторить</Trans>
                </UDButton>
              }
            />
          }
          empty={
            <UDEmptyState
              className="max-w-xl px-6"
              icon={<LayoutPanelTop size={28} />}
              title={t`Шаблонов пока нет`}
              subtitle={t`Добавьте свой шаблон или скопируйте готовый из каталога.`}
            />
          }
        >
          {templates.map((template, index) => (
            <TemplateItem key={template.id} template={template} index={index} />
          ))}
        </UDAsyncContent>
      </ListContainer>
      {createModal}
      {successCopyModal}
    </div>
  );
}

export default Templates;
